/**
 * Supabase Client Configuration
 * Provides authenticated access to Supabase backend
 */

import { createClient } from '@supabase/supabase-js';
import config from '../config';

const supabaseUrl = config.auth.supabase.url;
const supabaseAnonKey = config.auth.supabase.anonKey;

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase credentials not configured. Running in offline mode with localStorage.');
}

export const supabase = createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-key'
);

/**
 * Database Types
 * Auto-generated from Supabase schema
 */
export type Database = {
    public: {
        Tables: {
            profiles: {
                Row: {
                    id: string;
                    user_id: string;
                    name: string | null;
                    location: string | null;
                    certificate_type: 'PPL' | 'Instrument' | 'Commercial' | 'CFI';
                    total_budget: number;
                    target_date: string | null;
                    timezone: string;
                    currency: string;
                    created_at: string;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['profiles']['Row'], 'id' | 'created_at' | 'updated_at'>;
                Update: Partial<Database['public']['Tables']['profiles']['Insert']>;
            };
            expenses: {
                Row: {
                    id: string;
                    user_id: string;
                    date: string;
                    category: string;
                    amount: number;
                    description: string | null;
                    flight_hours: number | null;
                    receipt_url: string | null;
                    created_at: string;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['expenses']['Row'], 'id' | 'created_at' | 'updated_at'>;
                Update: Partial<Database['public']['Tables']['expenses']['Insert']>;
            };
            milestones: {
                Row: {
                    id: string;
                    user_id: string;
                    name: string;
                    status: 'not_started' | 'in_progress' | 'completed';
                    completed_date: string | null;
                    cost: number;
                    notes: string | null;
                    sort_order: number;
                    created_at: string;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['milestones']['Row'], 'id' | 'created_at' | 'updated_at'>;
                Update: Partial<Database['public']['Tables']['milestones']['Insert']>;
            };
            flight_hours: {
                Row: {
                    id: string;
                    user_id: string;
                    total_flight_hours: number;
                    total_ground_hours: number;
                    solo_hours: number;
                    cross_country_hours: number;
                    night_hours: number;
                    instrument_hours: number;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['flight_hours']['Row'], 'id' | 'updated_at'>;
                Update: Partial<Database['public']['Tables']['flight_hours']['Insert']>;
            };
        };
    };
};

/**
 * Helper Functions
 */

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
}

/**
 * Sign out current user
 */
export async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
}

export default supabase;
